import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {DomainGraphType, ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  OligoAwareFolderItemType,
  SchemaInterfaceExtendingSchema,
  TagSchema,
} from 'constants/RegistryConstants';
import AllStorablesSchemaSelector from 'samples/AllStorablesSchemaSelector';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
} from 'samples/storables/StorableSchemaConstants';
import {isSchemaTypeFilterOption, SchemaTypeFilterOption} from 'schemas/SchemaConstants';
import {isSchemaInterface} from 'schemas/SchemaPredicates';
import QueryingTagSchemaOrInterfaceSelector from 'schemas/shared/TagSchemaOrInterfaceSelector';
import {GRAY_DARK} from 'ui-library/Colors';
import DeleteIcon from 'ui-library/icons/DeleteIcon';

import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import {NonArrayDefinition} from './DefineTemplateVariablesEditor';

export type DomainDefinition = Extract<NonArrayDefinition, {kind: 'domain'}>;

export function isDomainDefinition(
  config: NonArrayDefinition | null | undefined
): config is DomainDefinition {
  return config != null && config.kind === 'domain' && Array.isArray(config.types);
}

const TAG_SCHEMA_BACKED_DOMAIN_TYPES: ReadonlySet<string> = new Set([
  DomainGraphType.CUSTOM_ENTITY,
  DomainGraphType.AA_SEQUENCE,
  DomainGraphType.DNA_SEQUENCE,
  DomainGraphType.RNA_SEQUENCE,
  DomainGraphType.MIXTURE,
  DomainGraphType.MOLECULE,
]);

const STORABLE_SCHEMA_BACKED_DOMAIN_TYPES: ReadonlySet<string> = new Set([
  DomainGraphType.PLATE,
  DomainGraphType.BOX,
  DomainGraphType.CONTAINER,
]);

type SchemaBackingType = 'tagSchema' | 'storableSchema' | 'none';

function getSchemaBackingType(type: string | null): SchemaBackingType {
  if (type == null) {
    return 'none';
  }
  if (TAG_SCHEMA_BACKED_DOMAIN_TYPES.has(type)) {
    return 'tagSchema';
  }
  if (STORABLE_SCHEMA_BACKED_DOMAIN_TYPES.has(type)) {
    return 'storableSchema';
  }
  return 'none';
}

function getOligoAwareFolderItemTypeForDomainType(type: string | null): OligoAwareFolderItemType | null {
  if (type == null || !TAG_SCHEMA_BACKED_DOMAIN_TYPES.has(type)) {
    return null;
  }
  const modelType = getModelTypeConstantForObjectType(type as ObjectType);
  return modelType as OligoAwareFolderItemType;
}

interface DomainTypeSchemaSelectorProps {
  config: DomainDefinition;
  onChangeConfig: (config: NonArrayDefinition) => void;
  disabled?: boolean;
}

/**
 * Component to allow setting schema or schema interface filters for domain type variables.
 *
 * When a domain type variable is defined, users can optionally restrict it to:
 * - A specific schema (schemaIds)
 * - A schema interface (schemaInterfaceIds) - only for TagSchema-backed types.
 *
 * For TagSchema-backed types:
 *   Uses QueryingTagSchemaOrInterfaceSelector which supports both schemas and interfaces.
 *
 * For StorableSchema-backed types:
 *   Uses AllStorablesSchemaSelector. Only plates are supported.
 *
 * For types without schemas:
 *   Returns null (no selector shown).
 */
export default function DomainTypeSchemaSelector({
  config,
  onChangeConfig,
  disabled,
}: DomainTypeSchemaSelectorProps): JSX.Element | null {
  const hasSchemaFilter = config.schemaIds != null && config.schemaIds.length > 0;
  const hasInterfaceFilter = config.schemaInterfaceIds != null && config.schemaInterfaceIds.length > 0;
  const hasFilter = hasSchemaFilter || hasInterfaceFilter;

  // config.types is an array with maxItems=1, so we just need the first element
  const selectedType = useMemo(() => config.types[0] ?? null, [config.types]);

  const schemaBackingType = useMemo(() => getSchemaBackingType(selectedType), [selectedType]);

  const oligoAwareFolderItemType = useMemo(
    () => getOligoAwareFolderItemTypeForDomainType(selectedType),
    [selectedType]
  );

  const selectedId = useMemo(() => {
    if (hasSchemaFilter) {
      return config.schemaIds?.[0] ?? null;
    }
    if (hasInterfaceFilter) {
      return config.schemaInterfaceIds?.[0] ?? null;
    }
    return null;
  }, [config.schemaIds, config.schemaInterfaceIds, hasSchemaFilter, hasInterfaceFilter]);

  if (schemaBackingType === 'none') {
    return null;
  }

  const handleTagSchemaChange = (selected: TagSchema | SchemaInterfaceExtendingSchema | null): void => {
    if (selected == null) {
      onChangeConfig({
        ...config,
        schemaIds: null,
        schemaInterfaceIds: null,
      });
    } else if (isSchemaInterface(selected)) {
      onChangeConfig({
        ...config,
        schemaIds: null,
        schemaInterfaceIds: [selected.id],
      });
    } else {
      onChangeConfig({
        ...config,
        schemaIds: [selected.id],
        schemaInterfaceIds: null,
      });
    }
  };

  // Currently only handles Plates and SchemaTypeFilterOptions, but this accepts the full union type for
  // type safety and future compatibility.
  const handleStorableSchemaChange = (
    selected: ContainerSchema | BoxSchema | LocationSchema | PlateSchema | SchemaTypeFilterOption
  ): void => {
    if (isSchemaTypeFilterOption(selected) || selected.id == null) {
      onChangeConfig({
        ...config,
        schemaIds: null,
        schemaInterfaceIds: null,
      });
      return;
    }
    onChangeConfig({
      ...config,
      schemaIds: [selected.id],
      schemaInterfaceIds: null,
    });
  };

  const handleClear = (): void => {
    onChangeConfig({
      ...config,
      schemaIds: null,
      schemaInterfaceIds: null,
    });
  };

  if (schemaBackingType === 'tagSchema' && oligoAwareFolderItemType != null) {
    return (
      <div className={css(styles.container)}>
        <VariableHeaderOrNull variableName="Schema filter" isCompact={false} />
        <div className={css(styles.infoText)}>
          Optionally restrict to items of a specific schema or schema interface.
        </div>
        <div className={css(styles.selectorRow)}>
          <div className={css(styles.selectorWrapper)}>
            <QueryingTagSchemaOrInterfaceSelector
              allowInterfaces={true}
              allowNone={true}
              oligoAwareFolderItemTypes={[oligoAwareFolderItemType]}
              selectedTagSchemaOrInterfaceId={selectedId}
              onChange={handleTagSchemaChange}
              isDisabled={disabled}
            />
          </div>
          {hasFilter && !disabled && (
            <div className={css(styles.clearIconWrapper)}>
              <DeleteIcon icon="cross-thick" onClick={handleClear} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (schemaBackingType === 'storableSchema') {
    return (
      <div className={css(styles.container)}>
        <VariableHeaderOrNull variableName="Schema filter" isCompact={false} />
        <div className={css(styles.infoText)}>Optionally restrict to plates of a specific schema.</div>
        <div className={css(styles.selectorRow)}>
          <div className={css(styles.selectorWrapper)}>
            <AllStorablesSchemaSelector
              includeStorableTypes={[ModelTypeConstants.PLATE]}
              selectedAllStorablesSchemaId={selectedId}
              onChange={handleStorableSchemaChange}
              shouldShowNoSchemaOption={true}
              isDisabled={disabled}
            />
          </div>
          {/* StorableSchemas don't support interfaces, so only check hasSchemaFilter */}
          {hasSchemaFilter && !disabled && (
            <div className={css(styles.clearIconWrapper)}>
              <DeleteIcon icon="cross-thick" onClick={handleClear} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    marginBottom: 8,
  },
  infoText: {
    fontSize: 11,
    color: GRAY_DARK,
    fontStyle: 'italic',
    marginBottom: 4,
  },
  selectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  selectorWrapper: {
    flex: 1,
  },
  clearIconWrapper: {
    cursor: 'pointer',
  },
});
